import React, { useMemo, useState, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import {
  VehicleGapDetectionProps,
  GapPoint,
  GapPosition,
} from './VehicleGapDetection.types';
import { VehicleView } from './VehicleView';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import { getVehiclePointsMap } from './pointsMapping';
import {
  MeasurementsMobileGrid,
  MeasurementsMobileGridItem,
} from '../shared/MeasurementsMobileGrid';
import { SliderNavigation } from '../VehiclePaintMeasurement/SliderNavigation';
import { useMobileStickyNavigation } from '@/hooks/useMobileStickyNavigation';

/**
 * Komponent wizualizacji pomiarów szczelin pojazdu
 *
 * Wyświetla 3 widoki pojazdu (lewy, górny, prawy) z oznaczonymi punktami kontrolnymi
 * oraz legendę statusów.
 *
 * @example
 * ```tsx
 * <VehicleGapDetection
 *   vehicleType="sedan"
 *   gapPoints={[
 *     { position: 'A', status: 'OK', view: 'left' },
 *     { position: 'B', status: 'NIE_OK', view: 'left' },
 *   ]}
 * />
 * ```
 */
export const VehicleGapDetection: React.FC<VehicleGapDetectionProps> = ({
  vehicleType,
  gapPoints,
  className,
}) => {
  const [hoveredPoint, setHoveredPoint] = useState<GapPosition | null>(null);
  const [selectedPoint, setSelectedPoint] = useState<GapPosition | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    isActive: isMobileNavigationActive,
    stickyTop: mobileNavigationStickyTop,
  } = useMobileStickyNavigation(sectionRef);

  const highlightedPoint = hoveredPoint ?? selectedPoint;

  const handlePointClick = useCallback((position: GapPosition) => {
    setSelectedPoint((prev) => (prev === position ? null : position));
  }, []);

  const handlePointHover = useCallback((position: GapPosition | null) => {
    setHoveredPoint(position);
  }, []);

  // Grupowanie punktów po widokach z dynamicznym określaniem widoku na podstawie vehicleType
  const groupedPoints = useMemo(() => {
    const groups: {
      left: GapPoint[];
      top: GapPoint[];
      right: GapPoint[];
    } = {
      left: [],
      top: [],
      right: [],
    };

    // Pobierz mapę punktów dla danego typu pojazdu
    const pointsMapForType = getVehiclePointsMap(vehicleType);

    gapPoints.forEach((point) => {
      // Sprawdź w której mapie (left/top/right) znajduje się ten punkt
      if (pointsMapForType.left[point.position]) {
        groups.left.push({ ...point, view: 'left' });
      } else if (pointsMapForType.top[point.position]) {
        groups.top.push({ ...point, view: 'top' });
      } else if (pointsMapForType.right[point.position]) {
        groups.right.push({ ...point, view: 'right' });
      }
      // Punkty bez zdefiniowanych współrzędnych dla tego typu pojazdu są pomijane
    });

    return groups;
  }, [gapPoints, vehicleType]);

  return (
    <div
      ref={sectionRef}
      className={clsx('relative w-full', className)}
      data-mobile-sticky-navigation-section="true"
    >
      <div
        aria-label="Nawigacja widoków pojazdu"
        aria-hidden={!isMobileNavigationActive}
        data-testid="gap-mobile-slider-navigation"
        role="navigation"
        style={{ top: mobileNavigationStickyTop }}
        className={clsx(
          'sticky z-30 h-[156px] transition-[top] duration-150 ease-out motion-reduce:transition-none bg-gradient-to-t from-[#0B2460] via-[#0B2460]/80 to-transparent lg:hidden',
          !isMobileNavigationActive && 'pointer-events-none invisible',
        )}
      >
        <SliderNavigation
          activeIndex={activeSlide}
          onNavigate={(index) => {
            swiperRef.current?.slideTo(index);
          }}
        />
      </div>
      {/* Nagłówek sekcji */}
      {/* <SectionSubtitle
        title="Pomiary szczelin"
        description="Kontrola dopasowania elementów karoserii pojazdu."
      /> */}

      {/* Widoki pojazdu */}
      <div
        className="relative -mt-[112px] overflow-hidden p-8 lg:mt-11 lg:pt-9"
        style={{
          background: 'linear-gradient(to top, #0B2460 0%, #111438 100%)',
        }}
      >
        <div className="mb-16 text-[18px] font-bold leading-[14px] text-white lg:mb-[54px] lg:text-[26px]">
          Pomiary szczelin
        </div>
        {/* Tło SVG Grid - inline SVG */}
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 40 0 V 40 H 0' fill='none' stroke='rgba(255,255,255,0.03)'/%3E%3C/svg%3E")`,
            backgroundSize: '40px 40px',
          }}
        />

        {/* Widoki pojazdu */}
        <div className="relative -mx-8 flex max-w-[1268px] flex-col lg:mx-0 lg:mb-10 lg:flex-row">
          {/* Desktop: Grid layout */}
          <div className="relative z-10 hidden flex-1 grid-cols-3 lg:grid">
            <div className="flex flex-col items-start">
              <VehicleView
                view="left"
                vehicleType={vehicleType}
                points={groupedPoints.left}
                label="Strona lewa"
                highlightedPoint={highlightedPoint}
                onPointHover={handlePointHover}
                onPointClick={handlePointClick}
              />
              <div className="lg:ml-[7%]">
                <div className="mb-8 mt-[82px] text-[18px] font-semibold leading-[14px] text-white">
                  Pomiary szczelin
                </div>
                <div className="inline-flex flex-wrap items-start overflow-hidden rounded-[15px] bg-navy-900">
                  {groupedPoints.left.map((point, index) => {
                    const isLast = index === groupedPoints.left.length - 1;
                    const statusLabel =
                      point.status === 'OK'
                        ? 'OK'
                        : point.status === 'NIE_OK'
                          ? 'NIE OK'
                          : 'BRAK';
                    return (
                      <div
                        key={point.position}
                        className={clsx(
                          'flex w-[90px] cursor-pointer flex-col items-center gap-2.5 px-1 py-4 text-center transition-colors lg:w-[60px] 2xl:w-[90px]',
                          !isLast && 'border-r',
                          selectedPoint === point.position && 'bg-blue-900/30',
                        )}
                        style={{ borderColor: '#353a6b' }}
                        onClick={() => handlePointClick(point.position)}
                        onMouseEnter={() => handlePointHover(point.position)}
                        onMouseLeave={() => handlePointHover(null)}
                      >
                        <div className="text-base font-medium leading-[14px] text-blue-300">
                          {point.position}
                        </div>
                        <div className="text-[13px] font-semibold leading-[14px] text-white">
                          {statusLabel}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center">
              <VehicleView
                view="top"
                vehicleType={vehicleType}
                points={groupedPoints.top}
                label=""
                highlightedPoint={highlightedPoint}
                onPointHover={handlePointHover}
                onPointClick={handlePointClick}
              />
              <div>
                <div
                  className="invisible mb-8 mt-[70px] text-[18px] font-semibold"
                  aria-hidden="true"
                >
                  &nbsp;
                </div>
                <div className="flex flex-wrap overflow-hidden rounded-[15px] bg-navy-900">
                  {groupedPoints.top.map((point, index) => {
                    const isLast = index === groupedPoints.top.length - 1;
                    const statusLabel =
                      point.status === 'OK'
                        ? 'OK'
                        : point.status === 'NIE_OK'
                          ? 'NIE OK'
                          : 'BRAK';
                    return (
                      <div
                        key={point.position}
                        className={clsx(
                          'flex w-[90px] cursor-pointer flex-col items-center gap-2.5 px-1 py-4 text-center transition-colors lg:w-[60px] 2xl:w-[90px]',
                          !isLast && 'border-r',
                          selectedPoint === point.position && 'bg-blue-900/30',
                        )}
                        style={{ borderColor: '#353a6b' }}
                        onClick={() => handlePointClick(point.position)}
                        onMouseEnter={() => handlePointHover(point.position)}
                        onMouseLeave={() => handlePointHover(null)}
                      >
                        <div className="text-base font-medium leading-[14px] text-blue-300">
                          {point.position}
                        </div>
                        <div className="text-[13px] font-semibold leading-[14px] text-white">
                          {statusLabel}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end">
              <VehicleView
                view="right"
                vehicleType={vehicleType}
                points={groupedPoints.right}
                label="Strona prawa"
                highlightedPoint={highlightedPoint}
                onPointHover={handlePointHover}
                onPointClick={handlePointClick}
              />
              <div className="lg:ml-[20%]">
                <div
                  className="invisible text-[18px] font-semibold leading-[14px]"
                  aria-hidden="true"
                >
                  &nbsp;
                </div>
                {/* Legenda -  pozycjonowana w prawym dolnym rogu */}
                <div className="relative right-[0px] z-20 mb-5 hidden flex-col items-end gap-3 lg:flex">
                  <div className="flex flex-row items-center gap-2">
                    <span className="font-archivo text-xs font-medium text-white">
                      (Optymalnie) OK
                    </span>
                    <SVGIcon name="check-circle" size="md" fill="white" />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="font-archivo text-xs font-medium text-white">
                      (Ostrzegawczo) NIE OK
                    </span>
                    <SVGIcon name="x" size="md" fill="red-800" />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="font-archivo text-xs font-medium text-white">
                      (Brak) Nie występuje
                    </span>

                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="12" cy="12" r="12" fill="#4B5B87" />
                      <path
                        d="M3 14.9105V9H5.4019C5.85532 9 6.23215 9.06734 6.53239 9.20202C6.83467 9.33478 7.06036 9.51659 7.20946 9.74747C7.3606 9.97836 7.43617 10.24 7.43617 10.5325C7.43617 10.773 7.38715 10.9788 7.28911 11.1501C7.19108 11.3194 7.05934 11.457 6.8939 11.5628C6.72846 11.6686 6.54362 11.7446 6.33938 11.7908V11.8485C6.56201 11.86 6.77544 11.9245 6.97968 12.0418C7.18597 12.1573 7.35447 12.3208 7.48519 12.5325C7.6159 12.7441 7.68126 13 7.68126 13.3001C7.68126 13.6061 7.60263 13.8812 7.44536 14.1255C7.28809 14.368 7.05117 14.5594 6.73459 14.6999C6.41801 14.8403 6.01974 14.9105 5.53977 14.9105H3ZM4.13661 14.0159H5.35901C5.77158 14.0159 6.06876 13.9418 6.25053 13.7937C6.43435 13.6436 6.52626 13.4512 6.52626 13.2165C6.52626 13.0414 6.48031 12.8836 6.3884 12.7431C6.29649 12.6008 6.16577 12.4892 5.99625 12.4084C5.82673 12.3256 5.62453 12.2843 5.38965 12.2843H4.13661V14.0159ZM4.13661 11.5137H5.26097C5.45705 11.5137 5.63372 11.48 5.79099 11.4127C5.94825 11.3434 6.07182 11.2463 6.16169 11.1212C6.2536 10.9942 6.29955 10.8442 6.29955 10.671C6.29955 10.442 6.21377 10.2535 6.04221 10.1053C5.87268 9.95719 5.62044 9.88312 5.28548 9.88312H4.13661V11.5137Z"
                        fill="white"
                      />
                      <path
                        d="M8.89064 14.9105V10.4776H9.96598V11.2165H10.015C10.1008 10.9606 10.2478 10.7633 10.4562 10.6248C10.6665 10.4844 10.9065 10.4141 11.1761 10.4141C11.2374 10.4141 11.3058 10.417 11.3814 10.4228C11.459 10.4266 11.5233 10.4334 11.5744 10.443V11.404C11.5274 11.3886 11.4529 11.3752 11.3507 11.3636C11.2507 11.3502 11.1537 11.3434 11.0597 11.3434C10.8575 11.3434 10.6757 11.3848 10.5144 11.4675C10.3551 11.5483 10.2295 11.6609 10.1375 11.8052C10.0456 11.9495 9.99968 12.1159 9.99968 12.3045V14.9105H8.89064Z"
                        fill="white"
                      />
                      <path
                        d="M13.8507 15C13.5525 15 13.2839 14.95 13.0449 14.8499C12.808 14.748 12.6201 14.5979 12.4812 14.3997C12.3444 14.2015 12.276 13.9572 12.276 13.6667C12.276 13.4165 12.325 13.2097 12.423 13.0462C12.5211 12.8826 12.6548 12.7518 12.8244 12.6537C12.9939 12.5556 13.1849 12.4815 13.3973 12.4315C13.6117 12.3795 13.8333 12.342 14.0621 12.3189C14.3378 12.292 14.5615 12.2679 14.733 12.2468C14.9046 12.2237 15.0292 12.189 15.1068 12.1429C15.1864 12.0948 15.2263 12.0207 15.2263 11.9206V11.9033C15.2263 11.6859 15.1578 11.5176 15.021 11.3983C14.8842 11.279 14.6871 11.2193 14.4297 11.2193C14.1581 11.2193 13.9426 11.2751 13.7833 11.3867C13.626 11.4983 13.5198 11.6301 13.4647 11.7821L12.4292 11.6436C12.5108 11.3742 12.6457 11.1491 12.8336 10.9683C13.0215 10.7855 13.2512 10.6489 13.5229 10.5584C13.7945 10.4661 14.0948 10.4199 14.4236 10.4199C14.6503 10.4199 14.876 10.4449 15.1007 10.4949C15.3253 10.545 15.5306 10.6277 15.7165 10.7431C15.9023 10.8567 16.0514 11.0115 16.1637 11.2078C16.2781 11.404 16.3353 11.6494 16.3353 11.9437V14.9105H15.2692V14.3016H15.2324C15.165 14.4247 15.07 14.5402 14.9475 14.6479C14.827 14.7537 14.6748 14.8393 14.491 14.9048C14.3092 14.9683 14.0958 15 13.8507 15ZM14.1387 14.2323C14.3613 14.2323 14.5543 14.191 14.7177 14.1082C14.8811 14.0236 15.0067 13.912 15.0945 13.7734C15.1844 13.6349 15.2293 13.4839 15.2293 13.3203V12.798C15.1946 12.8249 15.1354 12.8499 15.0516 12.873C14.9699 12.8961 14.878 12.9163 14.7759 12.9336C14.6738 12.9509 14.5727 12.9663 14.4726 12.9798C14.3725 12.9933 14.2857 13.0048 14.2122 13.0144C14.0468 13.0356 13.8987 13.0702 13.768 13.1183C13.6373 13.1664 13.5341 13.2338 13.4585 13.3203C13.383 13.405 13.3452 13.5147 13.3452 13.6494C13.3452 13.8418 13.4197 13.987 13.5688 14.0851C13.7179 14.1833 13.9079 14.2323 14.1387 14.2323Z"
                        fill="white"
                      />
                      <path
                        d="M18.7311 13.5195L18.728 12.2583H18.9057L20.5968 10.4776H21.8928L19.8126 12.6595H19.5828L18.7311 13.5195ZM17.7201 14.9105V9H18.8291V14.9105H17.7201ZM20.6734 14.9105L19.1416 12.8932L19.8891 12.1573L22 14.9105H20.6734Z"
                        fill="white"
                      />
                    </svg>
                  </div>
                </div>
                <div className="inline-flex flex-wrap items-start overflow-hidden rounded-[15px] bg-navy-900">
                  {groupedPoints.right.map((point, index) => {
                    const isLast = index === groupedPoints.right.length - 1;
                    const statusLabel =
                      point.status === 'OK'
                        ? 'OK'
                        : point.status === 'NIE_OK'
                          ? 'NIE OK'
                          : 'BRAK';
                    return (
                      <div
                        key={point.position}
                        className={clsx(
                          'flex w-[90px] cursor-pointer flex-col items-center gap-2.5 px-1 py-4 text-center transition-colors lg:w-[60px] 2xl:w-[90px]',
                          !isLast && 'border-r',
                          selectedPoint === point.position && 'bg-blue-900/30',
                        )}
                        style={{ borderColor: '#353a6b' }}
                        onClick={() => handlePointClick(point.position)}
                        onMouseEnter={() => handlePointHover(point.position)}
                        onMouseLeave={() => handlePointHover(null)}
                      >
                        <div className="text-base font-medium leading-[14px] text-blue-300">
                          {point.position}
                        </div>
                        <div className="text-[13px] font-semibold leading-[14px] text-white">
                          {statusLabel}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: Swiper slider */}
          <div className="relative z-10 -mb-8 w-full flex-1 pb-8 lg:mb-0 lg:hidden lg:pb-8">
            {/* Mobile: Legenda */}
            <div className="relative z-20 mx-8 mb-8 mt-[-30px] flex flex-row justify-between gap-3 rounded-[10px] border border-[#2a375e] bg-[#101740]/50 px-4 py-3 lg:hidden">
              <div className="flex flex-row-reverse items-center gap-2">
                <span className="font-archivo text-xs font-medium text-white">
                  OK
                  <br /> (Optymalnie)
                </span>
                <SVGIcon name="check-circle" size="md" fill="white" />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-right font-archivo text-xs font-medium text-white">
                  NIE OK <br /> (Ostrzegawczo)
                </span>
                <SVGIcon name="x" size="md" fill="red-800" />
              </div>
            </div>
            <div className="relative z-20 -mt-6 flex items-center justify-end gap-2 pr-[3.2rem]">
              <span className="text-right font-archivo text-xs font-medium text-white">
                BRAK <br /> (Nie występuje)
              </span>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#4B5B87" />
                <path
                  d="M3 14.9105V9H5.4019C5.85532 9 6.23215 9.06734 6.53239 9.20202C6.83467 9.33478 7.06036 9.51659 7.20946 9.74747C7.3606 9.97836 7.43617 10.24 7.43617 10.5325C7.43617 10.773 7.38715 10.9788 7.28911 11.1501C7.19108 11.3194 7.05934 11.457 6.8939 11.5628C6.72846 11.6686 6.54362 11.7446 6.33938 11.7908V11.8485C6.56201 11.86 6.77544 11.9245 6.97968 12.0418C7.18597 12.1573 7.35447 12.3208 7.48519 12.5325C7.6159 12.7441 7.68126 13 7.68126 13.3001C7.68126 13.6061 7.60263 13.8812 7.44536 14.1255C7.28809 14.368 7.05117 14.5594 6.73459 14.6999C6.41801 14.8403 6.01974 14.9105 5.53977 14.9105H3ZM4.13661 14.0159H5.35901C5.77158 14.0159 6.06876 13.9418 6.25053 13.7937C6.43435 13.6436 6.52626 13.4512 6.52626 13.2165C6.52626 13.0414 6.48031 12.8836 6.3884 12.7431C6.29649 12.6008 6.16577 12.4892 5.99625 12.4084C5.82673 12.3256 5.62453 12.2843 5.38965 12.2843H4.13661V14.0159ZM4.13661 11.5137H5.26097C5.45705 11.5137 5.63372 11.48 5.79099 11.4127C5.94825 11.3434 6.07182 11.2463 6.16169 11.1212C6.2536 10.9942 6.29955 10.8442 6.29955 10.671C6.29955 10.442 6.21377 10.2535 6.04221 10.1053C5.87268 9.95719 5.62044 9.88312 5.28548 9.88312H4.13661V11.5137Z"
                  fill="white"
                />
                <path
                  d="M8.89064 14.9105V10.4776H9.96598V11.2165H10.015C10.1008 10.9606 10.2478 10.7633 10.4562 10.6248C10.6665 10.4844 10.9065 10.4141 11.1761 10.4141C11.2374 10.4141 11.3058 10.417 11.3814 10.4228C11.459 10.4266 11.5233 10.4334 11.5744 10.443V11.404C11.5274 11.3886 11.4529 11.3752 11.3507 11.3636C11.2507 11.3502 11.1537 11.3434 11.0597 11.3434C10.8575 11.3434 10.6757 11.3848 10.5144 11.4675C10.3551 11.5483 10.2295 11.6609 10.1375 11.8052C10.0456 11.9495 9.99968 12.1159 9.99968 12.3045V14.9105H8.89064Z"
                  fill="white"
                />
                <path
                  d="M13.8507 15C13.5525 15 13.2839 14.95 13.0449 14.8499C12.808 14.748 12.6201 14.5979 12.4812 14.3997C12.3444 14.2015 12.276 13.9572 12.276 13.6667C12.276 13.4165 12.325 13.2097 12.423 13.0462C12.5211 12.8826 12.6548 12.7518 12.8244 12.6537C12.9939 12.5556 13.1849 12.4815 13.3973 12.4315C13.6117 12.3795 13.8333 12.342 14.0621 12.3189C14.3378 12.292 14.5615 12.2679 14.733 12.2468C14.9046 12.2237 15.0292 12.189 15.1068 12.1429C15.1864 12.0948 15.2263 12.0207 15.2263 11.9206V11.9033C15.2263 11.6859 15.1578 11.5176 15.021 11.3983C14.8842 11.279 14.6871 11.2193 14.4297 11.2193C14.1581 11.2193 13.9426 11.2751 13.7833 11.3867C13.626 11.4983 13.5198 11.6301 13.4647 11.7821L12.4292 11.6436C12.5108 11.3742 12.6457 11.1491 12.8336 10.9683C13.0215 10.7855 13.2512 10.6489 13.5229 10.5584C13.7945 10.4661 14.0948 10.4199 14.4236 10.4199C14.6503 10.4199 14.876 10.4449 15.1007 10.4949C15.3253 10.545 15.5306 10.6277 15.7165 10.7431C15.9023 10.8567 16.0514 11.0115 16.1637 11.2078C16.2781 11.404 16.3353 11.6494 16.3353 11.9437V14.9105H15.2692V14.3016H15.2324C15.165 14.4247 15.07 14.5402 14.9475 14.6479C14.827 14.7537 14.6748 14.8393 14.491 14.9048C14.3092 14.9683 14.0958 15 13.8507 15ZM14.1387 14.2323C14.3613 14.2323 14.5543 14.191 14.7177 14.1082C14.8811 14.0236 15.0067 13.912 15.0945 13.7734C15.1844 13.6349 15.2293 13.4839 15.2293 13.3203V12.798C15.1946 12.8249 15.1354 12.8499 15.0516 12.873C14.9699 12.8961 14.878 12.9163 14.7759 12.9336C14.6738 12.9509 14.5727 12.9663 14.4726 12.9798C14.3725 12.9933 14.2857 13.0048 14.2122 13.0144C14.0468 13.0356 13.8987 13.0702 13.768 13.1183C13.6373 13.1664 13.5341 13.2338 13.4585 13.3203C13.383 13.405 13.3452 13.5147 13.3452 13.6494C13.3452 13.8418 13.4197 13.987 13.5688 14.0851C13.7179 14.1833 13.9079 14.2323 14.1387 14.2323Z"
                  fill="white"
                />
                <path
                  d="M18.7311 13.5195L18.728 12.2583H18.9057L20.5968 10.4776H21.8928L19.8126 12.6595H19.5828L18.7311 13.5195ZM17.7201 14.9105V9H18.8291V14.9105H17.7201ZM20.6734 14.9105L19.1416 12.8932L19.8891 12.1573L22 14.9105H20.6734Z"
                  fill="white"
                />
              </svg>
            </div>
            {/* Left edge gradient */}
            <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-[89px] bg-gradient-to-r from-[#11153b]/90 to-[#11153b]/0" />
            {/* Right edge gradient */}
            <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-[89px] bg-gradient-to-l from-[#11153b]/90 to-[#11153b]/0" />
            <Swiper
              slidesPerView={1.2}
              spaceBetween={0}
              centeredSlides={true}
              className="w-full"
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              onSlideChange={(swiper) => {
                setActiveSlide(swiper.activeIndex);
              }}
            >
              <SwiperSlide>
                <VehicleView
                  view="left"
                  vehicleType={vehicleType}
                  points={groupedPoints.left}
                  label="Strona lewa"
                  highlightedPoint={highlightedPoint}
                  onPointHover={handlePointHover}
                  onPointClick={handlePointClick}
                />
                <div className="mb-8 mt-8 text-center text-[18px] font-semibold leading-[14px] text-white">
                  Pomiary szczelin
                </div>
                <MeasurementsMobileGrid
                  items={groupedPoints.left.map(
                    (point): MeasurementsMobileGridItem => ({
                      id: point.position,
                      label: point.position,
                      value:
                        point.status === 'OK'
                          ? 'OK'
                          : point.status === 'NIE_OK'
                            ? 'NIE OK'
                            : 'BRAK',
                    }),
                  )}
                  selectedItemId={selectedPoint}
                  onItemClick={(id) => handlePointClick(id as GapPosition)}
                  onItemHover={(id) =>
                    handlePointHover(id as GapPosition | null)
                  }
                />
              </SwiperSlide>

              <SwiperSlide>
                <VehicleView
                  view="top"
                  vehicleType={vehicleType}
                  points={groupedPoints.top}
                  label=""
                  highlightedPoint={highlightedPoint}
                  onPointHover={handlePointHover}
                  onPointClick={handlePointClick}
                />
                <div className="mb-8 mt-8 text-center text-[18px] font-semibold leading-[14px] text-white">
                  Pomiary szczelin
                </div>
                <MeasurementsMobileGrid
                  items={groupedPoints.top.map(
                    (point): MeasurementsMobileGridItem => ({
                      id: point.position,
                      label: point.position,
                      value:
                        point.status === 'OK'
                          ? 'OK'
                          : point.status === 'NIE_OK'
                            ? 'NIE OK'
                            : 'BRAK',
                    }),
                  )}
                  selectedItemId={selectedPoint}
                  onItemClick={(id) => handlePointClick(id as GapPosition)}
                  onItemHover={(id) =>
                    handlePointHover(id as GapPosition | null)
                  }
                />
              </SwiperSlide>

              <SwiperSlide>
                <VehicleView
                  view="right"
                  vehicleType={vehicleType}
                  points={groupedPoints.right}
                  label="Strona prawa"
                  highlightedPoint={highlightedPoint}
                  onPointHover={handlePointHover}
                  onPointClick={handlePointClick}
                />
                <div className="mb-8 mt-8 text-center text-[18px] font-semibold leading-[14px] text-white">
                  Pomiary szczelin
                </div>
                <MeasurementsMobileGrid
                  items={groupedPoints.right.map(
                    (point): MeasurementsMobileGridItem => ({
                      id: point.position,
                      label: point.position,
                      value:
                        point.status === 'OK'
                          ? 'OK'
                          : point.status === 'NIE_OK'
                            ? 'NIE OK'
                            : 'BRAK',
                    }),
                  )}
                  selectedItemId={selectedPoint}
                  onItemClick={handlePointClick}
                  onItemHover={handlePointHover}
                />
              </SwiperSlide>
            </Swiper>

            <div
              aria-hidden="true"
              aria-label="Nawigacja widoków pojazdu"
              data-testid="gap-inline-slider-navigation"
              role="navigation"
              className="pointer-events-none invisible lg:hidden"
            >
              <SliderNavigation
                activeIndex={activeSlide}
                onNavigate={(index) => {
                  swiperRef.current?.slideTo(index);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
